import Flex from 'antd/es/flex'
import Row from 'antd/es/grid/row'
import { IdeaSummaryCard } from './idea-summary-card.tsx'
import IdeyaTypography from '../../../../common/antd/ideya-typography.tsx'
import { ValueFormatterParams } from 'ag-grid-enterprise'
import {
  compactNumberFormatter,
  currencyValueFormatter,
  percentageValueFormatter,
} from '../../../../utils/formatters.ts'
import { useAppSelector } from '../../../../redux/hooks.ts'
import { selectThemeStyle } from '../../../../features/setting/setting.slice.ts'
import Spin from 'antd/es/spin/index'
import Col from 'antd/es/grid/col'
import { SummaryInfoHelp } from './idea-list-summary-info-help.tsx'
import { ReturnPill } from '@/common/components/return-pill.tsx'

interface IdeaSummaryDailyPnlProps {
  shouldShowSelectContributorMsg: boolean
  isLoading?: boolean
  dailyPnl: number
  totalBp: number
  wtd: number
  mtd: number
  qtd: number
  ytd: number
}

export const IdeaSummaryDailyPnlCard: React.FC<IdeaSummaryDailyPnlProps> = ({
  shouldShowSelectContributorMsg,
  isLoading,
  dailyPnl,
  totalBp,
  wtd,
  mtd,
  qtd,
  ytd,
}) => {
  const overlayComponent = shouldShowSelectContributorMsg ? (
    <SummaryInfoHelp />
  ) : isLoading ? (
    <Spin />
  ) : null

  return (
    <IdeaSummaryCard
      title={'Daily PnL'}
      overlayMsg={overlayComponent}
      textComponent={
        <div
          style={{
            width: '100%',
            display: 'flex',
            alignItems: 'flex-end',
          }}
        >
          <TextComponent dailyPnl={dailyPnl} totalBp={totalBp} />
        </div>
      }
      rows={[
        {
          label: 'WTD',
          value: <ValueRow value={wtd} percent={wtd / totalBp} />,
        },
        {
          label: 'MTD',
          value: <ValueRow value={mtd} percent={mtd / totalBp} />,
        },
        {
          label: 'QTD',
          value: <ValueRow value={qtd} percent={qtd / totalBp} />,
        },
        {
          label: 'YTD',
          value: <ValueRow value={ytd} percent={ytd / totalBp} />,
        },
      ]}
    ></IdeaSummaryCard>
  )
}

const TextComponent = ({
  dailyPnl,
  totalBp,
}: {
  dailyPnl: number
  totalBp: number
}) => {
  const themeStyle = useAppSelector(selectThemeStyle)
  const dailyPnlPercentRaw = dailyPnl / totalBp
  const dailyPnlPercent = Number.isNaN(dailyPnlPercentRaw)
    ? 0
    : dailyPnlPercentRaw

  return (
    <Row gutter={[10, 0]} align={'middle'} style={{ height: '100%' }}>
      <Col>
        <IdeyaTypography.Title level={3} style={{ marginBottom: 0 }}>
          {currencyValueFormatter({ value: dailyPnl } as ValueFormatterParams, {
            decimal: 0,
            currency: 'USD',
          })}
        </IdeyaTypography.Title>
      </Col>
      <Col>
        <ReturnPill
          value={dailyPnlPercent}
          text={percentageValueFormatter({
            value: Math.abs(dailyPnlPercent),
          } as ValueFormatterParams)}
          colors={{
            longBg: themeStyle.colorLongBg,
            shortBg: themeStyle.colorShortBg,
            longBorder: themeStyle.colorLongBorder,
            shortBorder: themeStyle.colorShortBorder,
            longText: themeStyle.colorLong,
            shortText: themeStyle.colorShort,
          }}
        />
      </Col>
    </Row>
  )
}

const ValueRow = ({ value, percent }: { value: number; percent: number }) => {
  const themeStyle = useAppSelector(selectThemeStyle)

  return (
    <>
      <Flex
        justify={'end'}
        gap={4}
        className={'idea-summary-daily-pnl__value'}
        style={{ whiteSpace: 'nowrap' }}
      >
        {compactNumberFormatter({ value } as ValueFormatterParams)}
        <div
          className={'idea-summary-daily-pnl__percent'}
          style={{
            minWidth: '2.5rem',
            textAlign: 'right',
            color:
              percent >= 0
                ? themeStyle.agGridTableGreen
                : themeStyle.agGridTableRed,
          }}
        >
          {percent > 0 ? '+' : ''}
          {percentageValueFormatter(
            {
              value: percent,
            } as ValueFormatterParams,
            { decimal: 1 }
          )}
        </div>
      </Flex>
    </>
  )
}
